import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type PropsWithChildren,
} from 'react';

import type { Panel } from '../types';
import { useCurrentPreviewPanel } from '../use-current-preview-panel';
import { usePreviewPanelsActions } from '../use-preview-panel-actions';

interface ProviderContext {
	canNavigateBack: boolean;
	history: Panel[];
	navigateBack: () => void;
	onPanelOpen: () => void;
}

interface ProviderProps {
	setHistory?: (history: Panel[]) => void;
}

const noop = () => {};

const PreviewPanelsNavigationContext = createContext<ProviderContext>({
	history: [],
	canNavigateBack: false,
	onPanelOpen: noop,
	navigateBack: noop,
});

export const PreviewPanelsNavigationProvider = ({
	children,
	setHistory,
}: PropsWithChildren<ProviderProps>): React.JSX.Element => {
	const [panelHistory, setPanelHistory] = useState<Panel[]>([]);

	const currentPanel = useCurrentPreviewPanel();
	const { open: openPreviewPanel } = usePreviewPanelsActions();

	const addHistoryItem = useCallback(
		(panel: Panel) => {
			const updatedPanelHistory = [...panelHistory, panel];
			setPanelHistory(updatedPanelHistory);
			setHistory?.(updatedPanelHistory);
		},
		[panelHistory, setHistory],
	);

	const removeLastHistoryItem = useCallback(() => {
		const previousItem = panelHistory.at(-1);

		if (previousItem == null) return null;

		const updatedPanelHistory = panelHistory.slice(0, -1);

		setPanelHistory(updatedPanelHistory);
		setHistory?.(updatedPanelHistory);

		return previousItem;
	}, [panelHistory, setHistory]);

	const clearHistory = useCallback(() => {
		setPanelHistory([]);
		setHistory?.([]);
	}, [setHistory]);

	const onPanelOpen = useCallback(() => {
		currentPanel != null && addHistoryItem(currentPanel);
	}, [currentPanel, addHistoryItem]);

	const navigateBack = useCallback(() => {
		const panelToOpen = removeLastHistoryItem();
		panelToOpen != null && openPreviewPanel({ panel: panelToOpen });
	}, [openPreviewPanel, removeLastHistoryItem]);

	const canNavigateBack = panelHistory.length > 0;

	useEffect(() => {
		// Ensure history is empty when preview panel is closed
		if (canNavigateBack && currentPanel == null) {
			clearHistory();
		}
	}, [canNavigateBack, currentPanel, clearHistory]);

	const context = useMemo(
		() => ({
			history: panelHistory,
			canNavigateBack,
			onPanelOpen,
			navigateBack,
		}),
		[canNavigateBack, panelHistory, onPanelOpen, navigateBack],
	);

	return (
		<PreviewPanelsNavigationContext.Provider value={context}>
			{children}
		</PreviewPanelsNavigationContext.Provider>
	);
};

export const usePreviewPanelsNavigation = (): ProviderContext =>
	useContext(PreviewPanelsNavigationContext);
